import React, { useState, useEffect, useRef, useCallback } from 'react';
import './RouteMapView.css';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const STARTING_ADDRESS = process.env.REACT_APP_GOOGLE_MAPS_STARTING_ADDRESS || '1319 Te Aroha Gordon Road, Manawaru 3391';

// Helper to add minutes to a time string (HH:MM format) and return new time string
function addMinutesToTime(timeStr, minutesToAdd) {
  if (!timeStr) return null;
  // Extract HH:MM from various datetime formats
  const match = String(timeStr).match(/(\d{2}):(\d{2})/);
  if (!match) return null;
  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const totalMinutes = hours * 60 + minutes + minutesToAdd;
  const newHours = Math.floor(totalMinutes / 60) % 24;
  const newMinutes = totalMinutes % 60;
  return `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
}

// Helper to format time with AM/PM
function formatTimeWithPeriod(timeStr) {
  if (!timeStr) return '';
  const [hours, minutes] = timeStr.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;
}

function RouteMapView({ deliveries = [], startingAddress = STARTING_ADDRESS, startTime = null, onEstimatedTimeChange = null, onRouteLegsCalculated = null }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const directionsServiceRef = useRef(null);
  const directionsRendererRef = useRef(null);
  const markersRef = useRef([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);

  // Load Google Maps API
  useEffect(() => {
    if (window.google && window.google.maps) {
      setIsLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=geometry,places`;
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      setIsLoaded(true);
    };
    
    script.onerror = () => {
      setError('Failed to load Google Maps. Please check your API key and internet connection.');
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup script if component unmounts before it loads
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  // Initialize map when API is loaded
  useEffect(() => {
    if (!isLoaded || !mapRef.current || mapInstanceRef.current) return;

    try {
      // Initialize the map centered on the starting address area (New Zealand)
      const initialCenter = { lat: -37.7879, lng: 175.2795 }; // Te Aroha area

      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        zoom: 11,
        center: initialCenter,
        mapTypeId: window.google.maps.MapTypeId.ROADMAP,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
      });

      // Initialize Directions service and renderer
      directionsServiceRef.current = new window.google.maps.DirectionsService();
      directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
        map: mapInstanceRef.current,
        suppressMarkers: true, // We'll add custom markers
        polylineOptions: {
          strokeColor: '#2563eb',
          strokeWeight: 4,
          strokeOpacity: 0.8,
        },
      });

      setError(null);
    } catch (err) {
      console.error('Error initializing map:', err);
      setError('Failed to initialize map');
    }
  }, [isLoaded]);

  // Calculate and display route when deliveries change
  useEffect(() => {
    if (!isLoaded || !directionsServiceRef.current || !directionsRendererRef.current) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    if (deliveries.length === 0) {
      directionsRendererRef.current.setDirections({ routes: [] });
      setRouteInfo(null);
      return;
    }

    // Group deliveries by order to combine multiple items from same order into single stop
    const orderGroups = {};
    deliveries.forEach((delivery) => {
      const orderId = delivery.customer_order_id;
      if (!orderGroups[orderId]) {
        orderGroups[orderId] = {
          customer: delivery.customer,
          order: delivery.order,
          items: [],
          maxPosition: 0
        };
      }
      // Add all items from this delivery to the group
      if (delivery.items) {
        delivery.items.forEach((item) => {
          const position = item.loading_position || 0;
          orderGroups[orderId].items.push(item);
          if (position > orderGroups[orderId].maxPosition) {
            orderGroups[orderId].maxPosition = position;
          }
        });
      }
    });

    // Stops are sorted by loading position - higher positions (back of trailer, back of truck) delivered first
    const routeStops = Object.values(orderGroups).sort((a, b) => {
      return b.maxPosition - a.maxPosition;
    });

    // Build waypoints array from route stops
    const waypoints = routeStops.map((stop) => ({
      location: stop.order?.delivery_address || stop.customer?.delivery_address,
      stopover: true,
    }));

    // Get the origin (starting address)
    const origin = startingAddress;

    // Get the destination (last delivery)
    const destination = waypoints[waypoints.length - 1].location;

    // Use only actual delivery locations as waypoints (exclude origin and destination)
    const intermediateWaypoints = waypoints.slice(0, -1);

    const request = {
      origin: origin,
      destination: destination,
      waypoints: intermediateWaypoints,
      optimizeWaypoints: false, // Keep the order as specified in itinerary
      travelMode: window.google.maps.TravelMode.DRIVING,
    };

    directionsServiceRef.current.route(request, (result, status) => {
      if (status === 'OK') {
        directionsRendererRef.current.setDirections(result);

        // Calculate total distance and time
        let totalDistance = 0;
        let totalDuration = 0;
        
        // Extract per-leg durations for itinerary ETAs
        const legDurations = [];
        
        if (result.routes[0] && result.routes[0].legs) {
          result.routes[0].legs.forEach(leg => {
            totalDistance += leg.distance.value;
            totalDuration += leg.duration.value;
            legDurations.push({
              duration: (leg.duration.value/60).toFixed(0), // in seconds converted to minutes
              distance: leg.distance.value, // in meters
              startAddress: leg.start_address,
              endAddress: leg.end_address
            });
          });
        }

        const stopCount = Object.keys(orderGroups).length;

        // Calculate return trip home (not displayed on map)
        const returnRequest = {
          origin: destination,
          destination: origin,
          travelMode: window.google.maps.TravelMode.DRIVING,
        };

        directionsServiceRef.current.route(returnRequest, (returnResult, returnStatus) => {
          let returnDuration = 0;
          if (returnStatus === 'OK' && returnResult.routes[0] && returnResult.routes[0].legs) {
            returnResult.routes[0].legs.forEach(leg => {
              returnDuration += leg.duration.value;
            });
          }

          const totalDurationMinutes = Math.round((totalDuration + returnDuration) / 60) + (stopCount * 15);
          setRouteInfo({
            distance: (totalDistance / 1000).toFixed(1), // km (outbound only)
            duration: totalDurationMinutes, // minutes + 15 min per stop for unloading
            stopCount: stopCount,
          });

          // Notify parent of estimated finish time
          if (onEstimatedTimeChange && startTime) {
            const estimatedFinish = addMinutesToTime(startTime, totalDurationMinutes);
            onEstimatedTimeChange(estimatedFinish);
          }
          
          // Notify parent of leg durations for per-stop ETA calculation
          if (onRouteLegsCalculated) {
            console.log('Calculated leg durations:', legDurations);
            onRouteLegsCalculated(legDurations);
          }
        });

        // Add custom markers (using routeStops for correct indexing)
        addCustomMarkers(result, routeStops);
      } else {
        console.error('Directions request failed:', status);
        setError(`Failed to calculate route: ${status}`);
        
        // Fallback: just show markers without route
        addMarkersWithoutRoute(routeStops);
      }
    });
  }, [isLoaded, deliveries, startingAddress, startTime]);

  // Add custom markers with info windows
  const addCustomMarkers = useCallback((directionsResult, stopsForMarkers) => {
    if (!mapInstanceRef.current || !directionsResult?.routes[0]?.legs) return;

    const map = mapInstanceRef.current;
    const legs = directionsResult.routes[0].legs;

    // Create info window for popup details
    const infoWindow = new window.google.maps.InfoWindow();

    // Add starting marker (green)
    const startMarker = new window.google.maps.Marker({
      position: legs[0].start_location,
      map: map,
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: '#22c55e',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
      },
      title: 'Start: ' + legs[0].start_address,
    });

    markersRef.current.push(startMarker);

    // Add delivery markers (skip the last leg as it ends at destination which is same as last stop)
    legs.forEach((leg, index) => {
      // Don't add marker for the last leg's end (it's the final destination)
      if (index >= stopsForMarkers.length) return;

      const stop = stopsForMarkers[index];
      const marker = new window.google.maps.Marker({
        position: leg.end_location,
        map: map,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: '#2563eb',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 3,
        },
        title: `Stop ${index + 1}: ${leg.end_address}`,
        label: {
          text: String(index + 1),
          color: '#ffffff',
          fontWeight: 'bold',
          fontSize: '10px',
        },
      });

      // Add click listener for info window
      marker.addListener('click', () => {
        const content = `
          <div class="marker-info-window">
            <strong>Stop ${index + 1}</strong><br/>
            <span>${leg.end_address}</span><br/>
            ${stop?.customer?.fullname ? `<span>${stop.customer.fullname}</span><br/>` : ''}
            ${stop?.items?.length ? `<span>${stop.items.length} item(s)</span>` : ''}
          </div>
        `;
        infoWindow.setContent(content);
        infoWindow.open(map, marker);
      });

      markersRef.current.push(marker);
    });

    // Fit map to show all markers
    fitMapToMarkers();
  }, []);

  // Fallback: add markers without route
  const addMarkersWithoutRoute = useCallback((stopsForMarkers) => {
    if (!mapInstanceRef.current) return;

    const map = mapInstanceRef.current;
    const bounds = new window.google.maps.LatLngBounds();
    const geocoder = new window.google.maps.Geocoder();

    // Add starting marker first
    geocoder.geocode({ address: startingAddress }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const startMarker = new window.google.maps.Marker({
          position: results[0].geometry.location,
          map: map,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: '#22c55e',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2,
          },
          title: 'Start: ' + startingAddress,
        });
        markersRef.current.push(startMarker);
        bounds.extend(results[0].geometry.location);

        // Then add delivery markers
        addDeliveryMarkers(geocoder, bounds, stopsForMarkers);
      }
    });
  }, [startingAddress]);

  // Helper to add delivery markers with geocoding
  const addDeliveryMarkers = useCallback((geocoder, bounds, stopsForMarkers) => {
    const map = mapInstanceRef.current;
    if (!map) return;

    const infoWindow = new window.google.maps.InfoWindow();
    let processedCount = 0;

    stopsForMarkers.forEach((stop, index) => {
      const address = stop.order?.delivery_address || stop.customer?.delivery_address;
      if (!address) {
        processedCount++;
        return;
      }

      geocoder.geocode({ address: address + ', New Zealand' }, (results, status) => {
        processedCount++;

        if (status === 'OK' && results[0]) {
          const marker = new window.google.maps.Marker({
            position: results[0].geometry.location,
            map: map,
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 12,
              fillColor: '#2563eb',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 3,
            },
            title: `Stop ${index + 1}: ${address}`,
            label: {
              text: String(index + 1),
              color: '#ffffff',
              fontWeight: 'bold',
              fontSize: '10px',
            },
          });

          marker.addListener('click', () => {
            const content = `
              <div class="marker-info-window">
                <strong>Stop ${index + 1}</strong><br/>
                <span>${address}</span><br/>
                ${stop?.customer?.fullname ? `<span>${stop.customer.fullname}</span><br/>` : ''}
              </div>
            `;
            infoWindow.setContent(content);
            infoWindow.open(map, marker);
          });

          markersRef.current.push(marker);
          bounds.extend(results[0].geometry.location);
        }

        // Fit map when all markers are processed
        if (processedCount === stopsForMarkers.length + 1) {
          fitMapToBounds(bounds);
        }
      });
    });
  }, []);

  // Fit map to show all markers
  const fitMapToMarkers = useCallback(() => {
    if (!mapInstanceRef.current || markersRef.current.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();
    markersRef.current.forEach(marker => {
      bounds.extend(marker.getPosition());
    });

    mapInstanceRef.current.fitBounds(bounds, { padding: 50 });
  }, []);

  // Fit map to provided bounds
  const fitMapToBounds = useCallback((bounds) => {
    if (!mapInstanceRef.current) return;
    mapInstanceRef.current.fitBounds(bounds, { padding: 50 });
  }, []);

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className="route-map-error">
        <div className="error-icon">⚠️</div>
        <p>Google Maps API key is not configured</p>
        <span className="hint">Set REACT_APP_GOOGLE_MAPS_API_KEY in your .env file</span>
      </div>
    );
  }

  return (
    <div className="route-map-container">
      <div ref={mapRef} className="route-map" />
      
      {error && (
        <div className="route-map-error-overlay">
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {routeInfo && !error && (
        <div className="route-info-panel">
          {/* <div className="route-info-item">
            <span className="route-info-icon">📍</span>
            <span className="route-info-value">{routeInfo.stopCount} stops</span>
          </div> */}
          <div className="route-info-item">
            <span className="route-info-icon">🛣️</span>
            <span className="route-info-value">{routeInfo.distance} km</span>
          </div>
          <div className="route-info-item">
            <span className="route-info-icon">⏱️</span>
            <span className="route-info-value">
              {routeInfo.duration >= 60 
                ? `~${Math.floor(routeInfo.duration / 60)}h ${routeInfo.duration % 60}m` 
                : `~${routeInfo.duration} min`} <br />round trip
            </span>
          </div>
        </div>
      )}

      {deliveries.length === 0 && isLoaded && !error && (
        <div className="route-map-empty-overlay">
          <span>Add deliveries to see the route</span>
        </div>
      )}
    </div>
  );
}

export default RouteMapView;
